import { defineStore } from 'pinia';
import { ref } from 'vue';
import { KycSessionStorage } from '@/common/KycSessionStorage';
import type { KycSessionData } from '@/stores/auth.store';
import { useImmersveAuthStore } from '@/stores/auth.store';
import { KycSession } from '@/models/KycSession';
import { KycFlowComparison } from '@/models/KycFlowComparison';
import * as Sentry from '@sentry/vue';

export const useKycSessionStore = defineStore('kycSessionStore', () => {
  const authStore = useImmersveAuthStore();
  const kycSession = ref<KycSession>();
  let lastFlowComparison: KycFlowComparison | undefined;

  function compareKycFlow(session: KycSession) {
    try {
      const comparison = KycFlowComparison.forKycSession(session, lastFlowComparison);
      if (!comparison) {
        return;
      }

      if (!comparison.isSameMismatchAs(lastFlowComparison)) {
        comparison.reportMismatch();
      }
      lastFlowComparison = comparison;
    } catch (error) {
      Sentry.captureException(error);
    }
  }

  async function loadSessionData() {
    const id = authStore.sessionId;
    if (!id) {
      throw new Error('No session id found');
    }

    if (!kycSession.value) {
      const sessionData = await KycSession.loadSessionData(id);
      kycSession.value = KycSession.fromApiResponse(sessionData);
      compareKycFlow(kycSession.value);
    } else {
      await kycSession.value.refresh();
      if (lastFlowComparison?.isOutdatedBy(kycSession.value)) {
        compareKycFlow(kycSession.value);
      }
    }

    Sentry.setTag('partnerAccountId', kycSession.value.partnerAccountId);
    Sentry.setTag('partnerName', kycSession.value.partner?.name);
    Sentry.setTag('oneSdkVersion', kycSession.value.oneSdkVersion);
    kycSession.value.onfidoSdkVersion &&
      Sentry.setTag('onfidoSdkVersion', kycSession.value.onfidoSdkVersion);

    KycSessionStorage.updateDataForSession<KycSessionData>(id, {
      redirectUrl: kycSession.value.redirectUrl,
      initialStepSet: kycSession.value.steps.initialStepSet,
    });
  }

  return {
    kycSession,
    getSessionData: loadSessionData,
  };
});
